/**
 * Jungle FX — global interaction layer for Enterprise.
 *
 * Every feature is opt-in from Theme settings > Jungle FX and is switched on by a class on <body>
 * (see snippets/ja-fx-head.liquid). Nothing here replaces Enterprise markup: features listen to
 * the theme's own events (on:variant:change, on:cart:add, on:line-item:change, ...) and only
 * enhance what is already on the page.
 */
(() => {
  if (window.jaFxLoaded) return;
  window.jaFxLoaded = true;

  const body = document.body;
  const has = (cls) => body.classList.contains(cls);
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const cartRoute = (window.theme && theme.routes && theme.routes.cart) || '/cart';
  const t = (key, fallback) => (window.jaFx && window.jaFx.strings && window.jaFx.strings[key]) || fallback;

  /* ---------------------------------------------------------------------------------------------
   * Shared cart cache (used by the quantity limiter and the cart side panel)
   * ------------------------------------------------------------------------------------------- */
  const jaCart = {
    data: null,
    promise: null,
    seq: 0,
    get(force = false) {
      if (!force && this.promise) return this.promise;
      // Several refreshes can overlap (quick quantity changes, the side panel adding items); only
      // the newest request may update the cache, so an older, slower answer never wins.
      const seq = ++this.seq;
      this.promise = fetch(`${cartRoute}.js`, { headers: { Accept: 'application/json' } })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (seq === this.seq) {
            if (data) this.data = data;
            return this.data;
          }
          // Overtaken by a newer request: wait for that one, so callers never act on an old cart.
          return this.promise;
        })
        .catch(() => this.data);
      return this.promise;
    },
    qtyFor(variantId) {
      if (!this.data || !this.data.items) return 0;
      return this.data.items
        .filter((item) => String(item.variant_id) === String(variantId))
        .reduce((sum, item) => sum + item.quantity, 0);
    }
  };
  window.jaCart = jaCart;

  // Back/forward restores re-render the cart drawer without a cart event, so re-sync then too.
  ['on:cart:add', 'on:line-item:change', 'on:bfcache:load-restore'].forEach((evtName) => {
    document.addEventListener(evtName, () => {
      jaCart.get(true).then(() => document.dispatchEvent(new CustomEvent('ja:cart:updated')));
    });
  });

  /* ---------------------------------------------------------------------------------------------
   * Quantity limiter — the product quantity can never exceed the stock still available to add
   * (stock minus what is already in the cart). Inputs are stamped server-side by
   * snippets/quantity-input.liquid with data-ja-stock / data-ja-variant.
   * ------------------------------------------------------------------------------------------- */
  if (has('ja-qty-limit')) {
    const noteFor = (qtyEl) => {
      const holder = qtyEl.closest('.product-info__add-to-cart') || qtyEl.parentElement;
      let note = holder.parentElement.querySelector(':scope > .ja-qty-note');
      if (!note) {
        note = document.createElement('p');
        note.className = 'ja-qty-note';
        note.setAttribute('role', 'status');
        note.setAttribute('aria-live', 'polite');
        holder.insertAdjacentElement('afterend', note);
      }
      return note;
    };

    // "Only X available" fades away after a few seconds; a background refresh (cart update,
    // variant change) never brings it back, only the shopper's next quantity change does.
    const NOTE_SECONDS = 5;
    const hideNoteLater = (note, message) => {
      clearTimeout(note.jaHideTimer);
      clearTimeout(note.jaFadeTimer);
      note.classList.remove('is-fading');
      note.jaHideTimer = setTimeout(() => {
        note.classList.add('is-fading');
        note.jaFadeTimer = setTimeout(() => {
          note.hidden = true;
          note.classList.remove('is-fading');
          note.dataset.jaFaded = message;
        }, 300);
      }, NOTE_SECONDS * 1000);
    };

    const applyLimit = (input, flash = false, fromUser = flash) => {
      const stock = parseInt(input.dataset.jaStock, 10);
      if (Number.isNaN(stock)) return;

      const qtyEl = input.closest('quantity-input');
      const plus = qtyEl && qtyEl.querySelector('[name="plus"]');
      const form = input.closest('form') || (input.form);
      const addBtn = form ? form.querySelector('[name="add"]') : null;
      const inCart = jaCart.qtyFor(input.dataset.jaVariant);
      const remaining = Math.max(stock - inCart, 0);
      const note = noteFor(qtyEl || input);

      if (remaining > 0) {
        input.max = remaining;
        if (parseInt(input.value, 10) > remaining) input.value = remaining;
      } else {
        input.max = 1;
        input.value = 1;
      }
      // Everything is already in the cart: "Buy it now" can't add more either.
      if (form) form.classList.toggle('ja-all-in-cart', remaining === 0 && stock > 0);

      // Plus is marked unavailable with aria-disabled only. A real disabled button anywhere in the
      // buy row makes Enterprise grey out and lock the whole quantity box, minus button included.
      const atMax = remaining === 0 || parseInt(input.value, 10) >= remaining;
      if (plus) {
        plus.disabled = false;
        if (atMax) plus.setAttribute('aria-disabled', 'true');
        else plus.removeAttribute('aria-disabled');
      }
      if (qtyEl) qtyEl.classList.toggle('ja-qty--at-max', atMax);

      if (addBtn) {
        if (remaining === 0) {
          addBtn.setAttribute('aria-disabled', 'true');
          addBtn.dataset.jaLocked = 'true';
        } else if (addBtn.dataset.jaLocked) {
          addBtn.removeAttribute('aria-disabled');
          delete addBtn.dataset.jaLocked;
        }
      }

      let message = '';
      if (remaining === 0 && stock > 0) {
        message = t('qtyAllInCart', 'All available stock is already in your cart.');
      } else if (atMax && remaining > 0 && (flash || input.dataset.jaTouched)) {
        // The stock bar already shows low stock, so only explain the limit once the shopper has
        // picked the maximum (or tries to go past it).
        message = t('qtyMax', 'Maximum available: [quantity]').replace('[quantity]', remaining);
      }
      const isMaxNote = Boolean(message) && remaining > 0;
      if (isMaxNote && !fromUser && note.dataset.jaFaded === message) message = '';
      if (!isMaxNote || fromUser) delete note.dataset.jaFaded;

      const showing = !note.hidden && note.textContent === message;
      if (note.textContent !== message && message) note.textContent = message;
      note.hidden = !message;
      if (!message || !isMaxNote) {
        clearTimeout(note.jaHideTimer);
        clearTimeout(note.jaFadeTimer);
        note.classList.remove('is-fading');
      } else if (fromUser || !showing) {
        hideNoteLater(note, message);
      }
      if (flash && message) {
        note.classList.remove('ja-qty-note--flash');
        // Restart the attention animation.
        void note.offsetWidth; // eslint-disable-line no-void
        note.classList.add('ja-qty-note--flash');
      }
    };

    const applyAll = () => {
      document.querySelectorAll('input.qty-input__input[data-ja-stock]').forEach((input) => applyLimit(input));
    };

    document.addEventListener('input', (evt) => {
      const input = evt.target;
      if (!input.matches || !input.matches('input.qty-input__input[data-ja-stock]')) return;
      input.dataset.jaTouched = '1';
      const max = parseInt(input.max, 10);
      if (!Number.isNaN(max) && parseInt(input.value, 10) > max) {
        input.value = max;
        applyLimit(input, true);
      }
    });

    document.addEventListener('change', (evt) => {
      const input = evt.target;
      if (input.matches && input.matches('input.qty-input__input[data-ja-stock]')) {
        input.dataset.jaTouched = '1';
        applyLimit(input, false, true);
      }
    });

    // Plus at the limit: stop the extra step (runs before Enterprise's own click handler) and show
    // the "maximum available" note instead of silently doing nothing. Minus is never blocked.
    document.addEventListener('click', (evt) => {
      const plus = evt.target.closest && evt.target.closest('quantity-input [name="plus"][aria-disabled="true"]');
      if (!plus) return;
      const input = plus.closest('quantity-input').querySelector('input[data-ja-stock]');
      if (!input) return;
      evt.preventDefault();
      evt.stopPropagation();
      applyLimit(input, true);
    }, true);

    document.addEventListener('on:variant:change', () => requestAnimationFrame(applyAll));
    document.addEventListener('on:quickbuy:after-open', () => setTimeout(applyAll, 60));
    // Quick add loads the product after the drawer has opened (slow connections can take a while),
    // so apply the limit whenever its contents change.
    const quickAdd = document.querySelector('quick-add-drawer');
    if (quickAdd) {
      let queued = false;
      new MutationObserver(() => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          applyAll();
        });
      }).observe(quickAdd, { childList: true, subtree: true });
    }
    document.addEventListener('ja:cart:updated', applyAll);

    jaCart.get().then(applyAll);
  }

  /* ---------------------------------------------------------------------------------------------
   * Cart drawer checkout with collection-only (noship) or paid-courier-only (tcg) items. The cart
   * page asks shoppers to confirm this once per session before checkout (ja-cart-page.js); an
   * unconfirmed checkout from the drawer goes to the cart page, which opens that confirmation.
   * The drawer's notice (snippets/free-shipping-notice.liquid) carries the cart's shipping state.
   * ------------------------------------------------------------------------------------------- */
  document.addEventListener('submit', (evt) => {
    const form = evt.target;
    if (!form || form.id !== 'cart-drawer-form' || evt.defaultPrevented) return;
    if (evt.submitter && evt.submitter.name !== 'checkout') return;
    const marker = document.querySelector('#cart-drawer [data-ja-ship-state]');
    const state = marker && marker.dataset.jaShipState;
    if (state !== 'noship' && state !== 'tcg') return;
    const ids = Array.from(new Set((marker.dataset.jaShipIds || '').split(',').filter(Boolean)));
    if (!ids.length) return;
    let confirmed = false;
    try {
      confirmed = sessionStorage.getItem('ja-cart-shipping-ack') === `${state}:${ids.sort().join(',')}`;
    } catch (error) {
      confirmed = false;
    }
    if (confirmed) return;
    evt.preventDefault();
    window.location.href = `${cartRoute}#ja-shipping-check`;
  }, true);

  /* ---------------------------------------------------------------------------------------------
   * "Email when available" card buttons (snippets/product-card.liquid). The Back in Stock form
   * needs the product's details, which are fetched only when a shopper is about to use the
   * button (hover, focus, touch) instead of being embedded in every sold out card. A click that
   * arrives first waits for them, then is handed to the app; if they can't load, the button's
   * link opens the product page, which has its own form.
   * ------------------------------------------------------------------------------------------- */
  {
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const loadBis = (btn) => {
      if (btn.dataset.productData) return Promise.resolve(true);
      if (!btn.jaBisLoading) {
        btn.jaBisLoading = fetch(`${root}products/${encodeURIComponent(btn.dataset.productHandle)}.js`, { headers: { Accept: 'application/json' } })
          .then((res) => (res.ok ? res.json() : null))
          .then((product) => {
            if (product) btn.setAttribute('data-product-data', JSON.stringify(product));
            else btn.jaBisLoading = null;
            return Boolean(product);
          })
          .catch(() => {
            btn.jaBisLoading = null;
            return false;
          });
      }
      return btn.jaBisLoading;
    };
    const bisFrom = (evt) => evt.target.closest && evt.target.closest('.ja-bis-btn[data-product-handle]');
    ['pointerover', 'focusin', 'touchstart'].forEach((name) => {
      document.addEventListener(name, (evt) => {
        const btn = bisFrom(evt);
        if (btn) loadBis(btn);
      }, { passive: true });
    });
    // Capture on window so this runs before the app's own click handling.
    window.addEventListener('click', (evt) => {
      const btn = bisFrom(evt);
      if (!btn || btn.dataset.productData) return;
      evt.preventDefault();
      evt.stopImmediatePropagation();
      loadBis(btn).then((ok) => {
        if (ok) btn.click();
        else window.location.href = btn.href;
      });
    }, true);
  }

  /* ---------------------------------------------------------------------------------------------
   * Cart drawer item count badge — the theme only re-renders the drawer's items and summary when a
   * line changes, so keep the badge in the drawer header in step with the cart.
   * ------------------------------------------------------------------------------------------- */
  if (has('ja-cart-style')) {
    document.addEventListener('ja:cart:updated', () => {
      const count = jaCart.data && typeof jaCart.data.item_count === 'number' ? jaCart.data.item_count : null;
      if (count === null) return;
      document.querySelectorAll('#cart-drawer .ja-cart-count').forEach((badge) => {
        badge.textContent = count;
        badge.hidden = count === 0;
        const label = count === 1 ? t('cartCountOne', '1 item') : t('cartCountMany', '2 items').replace(/\d+/, count);
        badge.setAttribute('aria-label', label);
      });
    });
  }

  /* ---------------------------------------------------------------------------------------------
   * Cart drawer — line items glide in when the drawer opens, but not on every refresh.
   * ------------------------------------------------------------------------------------------- */
  if (has('ja-cart-style') && !reduceMotion) {
    const drawer = document.getElementById('cart-drawer');
    if (drawer) {
      let wasOpen = drawer.hasAttribute('open');
      new MutationObserver(() => {
        const isOpen = drawer.hasAttribute('open');
        if (isOpen && !wasOpen) {
          drawer.classList.add('ja-cart-entering');
          clearTimeout(drawer.jaEnterTimer);
          drawer.jaEnterTimer = setTimeout(() => drawer.classList.remove('ja-cart-entering'), 1100);
        }
        wasOpen = isOpen;
      }).observe(drawer, { attributes: true, attributeFilter: ['open'] });
    }
  }

  /* ---------------------------------------------------------------------------------------------
   * Back to top — floating button with a scroll progress ring.
   * ------------------------------------------------------------------------------------------- */
  const backToTop = document.querySelector('.ja-back-to-top');
  if (backToTop) {
    const ring = backToTop.querySelector('.ja-back-to-top__progress');
    const length = ring ? (parseFloat(ring.getAttribute('pathLength')) || ring.getTotalLength()) : 0;
    if (ring) {
      ring.style.strokeDasharray = `${length} ${length}`;
      ring.style.strokeDashoffset = length;
    }
    const threshold = parseInt(backToTop.dataset.threshold || '600', 10);
    let ticking = false;
    const update = () => {
      ticking = false;
      const scrollTop = window.scrollY;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      backToTop.classList.toggle('is-visible', scrollTop > threshold);
      if (ring && max > 0) ring.style.strokeDashoffset = length - (scrollTop / max) * length;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    }, { passive: true });
    backToTop.addEventListener('click', () => {
      if (window.jaSmoothScroll) window.jaSmoothScroll.stop();
      window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
    });
    update();

    // Product pages: rise above the sticky "Add to cart" bar only while that bar is really on
    // screen (Enterprise also hides it near the page bottom) and sits under the button. The lift
    // is measured from the bar itself and eases in with the button's own slide.
    const panelHost = document.querySelector('sticky-atc-panel')?.parentElement;
    if (panelHost) {
      let queued = false;
      const lift = () => {
        queued = false;
        const panel = panelHost.querySelector('sticky-atc-panel');
        let amount = 0;
        if (panel && !panel.classList.contains('sticky-atc-panel--out')
          && !body.classList.contains('scrolled-to-bottom')
          && getComputedStyle(panel).display !== 'none') {
          const bar = panel.getBoundingClientRect();
          const btn = backToTop.getBoundingClientRect();
          if (bar.width && bar.left < btn.right + 8 && bar.right > btn.left - 8) {
            const gap = parseFloat(getComputedStyle(panel).marginBottom) || 0;
            const bottom = parseFloat(getComputedStyle(backToTop).bottom) || 0;
            amount = Math.max(Math.ceil(panel.offsetHeight + gap + 12 - bottom), 0);
          }
        }
        backToTop.style.setProperty('--ja-btt-lift', `${amount}px`);
      };
      const queue = () => {
        if (!queued) {
          queued = true;
          requestAnimationFrame(lift);
        }
      };
      // Watch only what decides the bar's visibility: body classes, the bar's own class, and the
      // bar being swapped for a new one (variant changes re-render product markup).
      const watcher = new MutationObserver(() => {
        const panel = panelHost.querySelector(':scope > sticky-atc-panel');
        if (panel && panel !== watched) {
          watched = panel;
          watcher.observe(panel, { attributes: true, attributeFilter: ['class'] });
        }
        queue();
      });
      let watched = panelHost.querySelector(':scope > sticky-atc-panel');
      watcher.observe(body, { attributes: true, attributeFilter: ['class'] });
      watcher.observe(panelHost, { childList: true });
      if (watched) watcher.observe(watched, { attributes: true, attributeFilter: ['class'] });
      window.addEventListener('on:debounced-resize', queue);
      queue();
    }
  }

  /* ---------------------------------------------------------------------------------------------
   * Smooth scroll — eases notched mouse-wheel scrolling on desktop (Lenis-style).
   * - Trackpads and Magic Mouse already glide with native momentum, so their gestures are left
   *   entirely to the browser (easing them on top is what makes scrolling feel sticky).
   * - Control goes back to the browser when a drawer, modal or search overlay has locked the page,
   *   when the wheel is over anything that scrolls on its own, for pinch-zoom and sideways swipes.
   * - Small page shifts while easing (lazy images, the sticky header tucking its menu away) are
   *   absorbed instead of cutting the scroll short; a real jump (keyboard, anchor link, scrollbar
   *   drag) hands over to the browser straight away.
   * ------------------------------------------------------------------------------------------- */
  if (has('ja-smooth') && finePointer && !reduceMotion) {
    const html = document.documentElement;
    const lockClasses = ['overflow-hidden', 'fixed', 'overlay-predictive-search'];
    const state = {
      current: window.scrollY,
      target: window.scrollY,
      running: false,
      lastSet: null,
      ease: parseFloat((window.jaFx && window.jaFx.smoothEase) || '0.12') || 0.12
    };
    const gesture = { lastTime: 0, native: false, target: null, ownScroll: false, styleLocked: false };

    // Theme locks use body classes (checked on every event, cheap); apps that lock the page with
    // overflow: hidden are caught by a computed-style check once per gesture.
    const isLocked = () => lockClasses.some((cls) => body.classList.contains(cls)) || gesture.styleLocked;
    const styleLocked = () => getComputedStyle(body).overflowY === 'hidden'
      || getComputedStyle(html).overflowY === 'hidden';

    const scrollsOnItsOwn = (start) => {
      let el = start;
      while (el && el !== body && el !== html) {
        if (el.nodeType === 1) {
          if (el.hasAttribute('data-ja-smooth-prevent') || el.matches('[aria-modal="true"], dialog, .drawer, .modal, iframe')) return true;
          const style = getComputedStyle(el);
          if (/(auto|scroll)/.test(style.overflowY) && el.scrollHeight > el.clientHeight + 1) return true;
        }
        el = el.parentElement;
      }
      return false;
    };

    // Trackpad / Magic Mouse: fractional or small pixel deltas, or Chrome/Safari's wheelDelta
    // pattern for precise scrolling. Anything unsure is left to the browser.
    const isPreciseScroll = (evt) => {
      if (evt.deltaMode !== 0) return false;
      if (!Number.isInteger(evt.deltaY)) return true;
      if (typeof evt.wheelDeltaY === 'number' && evt.wheelDeltaY !== 0) {
        if (evt.wheelDeltaY === -3 * evt.deltaY) return true;
        if (Math.abs(evt.wheelDeltaY) % 120 !== 0) return true;
      }
      return Math.abs(evt.deltaY) < 40;
    };

    const stop = () => {
      state.running = false;
      state.current = window.scrollY;
      state.target = window.scrollY;
    };

    const frame = () => {
      if (!state.running) return;
      state.current += (state.target - state.current) * state.ease;
      if (Math.abs(state.target - state.current) < 0.5) state.current = state.target;
      state.lastSet = state.current;
      window.scrollTo(0, state.current);
      if (state.current === state.target) {
        stop();
        return;
      }
      requestAnimationFrame(frame);
    };

    window.addEventListener('wheel', (evt) => {
      if (evt.defaultPrevented || evt.ctrlKey || evt.metaKey) return;

      // Decide once per gesture (a pause of 150ms starts a new one) so a swipe never switches
      // between native and eased scrolling halfway through.
      const now = evt.timeStamp || performance.now();
      if (now - gesture.lastTime > 150) {
        gesture.native = isPreciseScroll(evt);
        gesture.target = null;
        gesture.styleLocked = !gesture.native && styleLocked();
      }
      gesture.lastTime = now;

      if (gesture.native || Math.abs(evt.deltaX) > Math.abs(evt.deltaY)) {
        if (state.running) stop();
        return;
      }
      if (evt.target !== gesture.target) {
        gesture.target = evt.target;
        gesture.ownScroll = scrollsOnItsOwn(evt.target);
      }
      if (gesture.ownScroll || isLocked()) {
        if (state.running) stop();
        return;
      }

      evt.preventDefault();
      let delta = evt.deltaY;
      if (evt.deltaMode === 1) delta *= 40;
      else if (evt.deltaMode === 2) delta *= window.innerHeight;

      if (!state.running) {
        state.current = window.scrollY;
        state.target = window.scrollY;
      }
      const max = Math.max(html.scrollHeight - window.innerHeight, 0);
      state.target = Math.min(Math.max(state.target + delta, 0), max);

      if (!state.running) {
        state.running = true;
        requestAnimationFrame(frame);
      }
    }, { passive: false });

    window.addEventListener('scroll', () => {
      if (!state.running) {
        state.current = window.scrollY;
        state.target = window.scrollY;
        return;
      }
      const shift = window.scrollY - state.lastSet;
      if (state.lastSet === null || Math.abs(shift) <= 1) return;
      if (Math.abs(shift) > 160) {
        stop();
        return;
      }
      // The browser nudged the page (scroll anchoring); carry on from where it put us.
      state.current += shift;
      state.target += shift;
      state.lastSet = window.scrollY;
    }, { passive: true });
    ['keydown', 'pointerdown', 'touchstart'].forEach((evtName) => {
      window.addEventListener(evtName, () => { if (state.running) stop(); }, { passive: true });
    });

    window.jaSmoothScroll = { stop };
  }

  /* ---------------------------------------------------------------------------------------------
   * Reveal on scroll for Jungle FX sections ([data-ja-reveal]). html.ja-js-reveal is set inline
   * in <head> (snippets/ja-fx-head.liquid) with a timeout that removes it if this file never
   * runs, so content can never stay invisible.
   * ------------------------------------------------------------------------------------------- */
  if (has('ja-reveal')) {
    const html = document.documentElement;

    if (reduceMotion || !('IntersectionObserver' in window) || (window.Shopify && Shopify.designMode)) {
      html.classList.remove('ja-js-reveal');
    } else {
      // Cards inside a sideways carousel reveal together as soon as the carousel comes on screen.
      // Watching each card would leave the ones waiting off to the side blank until they are
      // swiped or arrowed into view, then pop in late.
      const trackSelector = '.ja-track--scroll, .ja-track--grid-scroll-mob';
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          if (entry.target.matches(trackSelector)) {
            entry.target.dataset.jaRevealed = '1';
            entry.target.querySelectorAll('[data-ja-reveal]:not(.is-revealed)').forEach((card) => card.classList.add('is-revealed'));
          } else {
            entry.target.classList.add('is-revealed');
          }
          observer.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.01 });

      const watch = (scope = document) => {
        scope.querySelectorAll('[data-ja-reveal]:not(.is-revealed):not([data-ja-watched])').forEach((el) => {
          el.dataset.jaWatched = '1';
          const track = el.parentElement && el.parentElement.closest(trackSelector);
          if (track) {
            if (track.dataset.jaRevealed) {
              el.classList.add('is-revealed');
            } else if (!track.dataset.jaWatched) {
              track.dataset.jaWatched = '1';
              observer.observe(track);
            }
            return;
          }
          observer.observe(el);
        });
      };
      watch();
      document.addEventListener('DOMContentLoaded', () => watch());
      document.addEventListener('shopify:section:load', (evt) => watch(evt.target));
      window.addEventListener('ja:reveal:refresh', () => watch());
    }
  }

  /* ---------------------------------------------------------------------------------------------
   * Carousel slides that start off to the side show straight away. Enterprise's animate-on-scroll
   * reveals each slide only once it comes into view, so swiping or arrowing through a carousel
   * showed blank cards that faded and rose into place late, one after another. Slides that are on
   * screen when the carousel arrives keep their reveal. Carousels added later (recommendations,
   * recently viewed, theme editor) are picked up by the mutation watcher.
   * ------------------------------------------------------------------------------------------- */
  {
    const settleCarousels = () => {
      document.querySelectorAll('carousel-slider .slider, .slider--mobile').forEach((slider) => {
        const box = slider.getBoundingClientRect();
        // Hidden (closed drawer, inactive tab) or not scrolling at this size: check again later.
        if (!box.width || slider.scrollWidth <= slider.clientWidth + 1) return;
        slider.querySelectorAll('[data-cc-animate]:not(.cc-animate-in):not([data-ja-settled])').forEach((el) => {
          el.dataset.jaSettled = '1';
          const rect = el.getBoundingClientRect();
          if (rect.left >= box.right - 1 || rect.right <= box.left + 1) {
            el.classList.add('cc-animate-in', 'ja-anim-instant');
          }
        });
      });
    };
    let settleTimer;
    const queueSettle = () => {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(settleCarousels, 150);
    };
    settleCarousels();
    document.addEventListener('DOMContentLoaded', settleCarousels);
    window.addEventListener('load', queueSettle);
    window.addEventListener('on:debounced-resize', queueSettle);
    document.addEventListener('shopify:section:load', queueSettle);
    new MutationObserver(queueSettle).observe(body, { childList: true, subtree: true });
  }

  /* ---------------------------------------------------------------------------------------------
   * Image fade-in — below-the-fold images that have not loaded yet fade in once they arrive.
   * Images already on screen are never hidden, so the first paint is untouched.
   * ------------------------------------------------------------------------------------------- */
  if (has('ja-img-fade') && !reduceMotion) {
    const fold = window.innerHeight * 1.1;
    const done = (img) => {
      img.classList.add('ja-img-loaded');
      img.classList.remove('ja-img-pending');
    };
    document.querySelectorAll('main img').forEach((img) => {
      if (img.complete || img.closest('.ja-no-fade, .media-gallery, .slideshow, .ja-hero, .video-section, .video-played-hidden')) return;
      if (img.getBoundingClientRect().top < fold) return;
      img.classList.add('ja-img-pending');
      img.addEventListener('load', () => done(img), { once: true });
      img.addEventListener('error', () => done(img), { once: true });
      if (img.complete) done(img);
    });
  }

  /* ---------------------------------------------------------------------------------------------
   * Header scroll state — adds .ja-scrolled to <body> once the page has moved, used for the
   * sticky header shadow (and the optional frosted glass).
   * ------------------------------------------------------------------------------------------- */
  {
    let scrolled = false;
    const onScroll = () => {
      const next = window.scrollY > 40;
      if (next !== scrolled) {
        scrolled = next;
        body.classList.toggle('ja-scrolled', scrolled);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------------------------------------------------------------------------------------------
   * Button text flip — the label is drawn with two text-shadows so it survives Enterprise
   * rewriting button text (variant changes, "Sold out" etc). Buttons whose label wraps onto more
   * than one line are excluded because the second copy would show through.
   * ------------------------------------------------------------------------------------------- */
  if (has('ja-btn-flip') && finePointer) {
    const flipSelector = '.btn--primary, .btn--secondary';
    // The flip copy sits 3.6em below the label, which hides one- and two-line labels completely;
    // anything taller simply keeps the colour sweep without the text flip.
    const check = (btn) => {
      const cs = getComputedStyle(btn);
      const lh = parseFloat(cs.lineHeight) || parseFloat(cs.fontSize) * 1.2 || 18;
      const pad = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom) + parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
      btn.classList.toggle('ja-noflip', btn.offsetHeight - pad > lh * 2.4);
    };
    const checkAll = () => {
      const btns = Array.from(document.querySelectorAll(flipSelector));
      const tall = btns.map((btn) => {
        const cs = getComputedStyle(btn);
        const lh = parseFloat(cs.lineHeight) || parseFloat(cs.fontSize) * 1.2 || 18;
        const pad = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom) + parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
        return btn.offsetHeight - pad > lh * 2.4;
      });
      btns.forEach((btn, i) => btn.classList.toggle('ja-noflip', tall[i]));
    };
    window.addEventListener('on:debounced-resize', checkAll);
    document.addEventListener('pointerover', (evt) => {
      const btn = evt.target.closest && evt.target.closest(flipSelector);
      if (btn && !btn.dataset.jaFlipChecked) {
        btn.dataset.jaFlipChecked = '1';
        check(btn);
      }
    }, { passive: true });
    if (document.readyState === 'complete') checkAll();
    else window.addEventListener('load', checkAll, { once: true });
  }

  /* ---------------------------------------------------------------------------------------------
   * Click ripple — a soft circle spreads from the exact press point (flat design, no shadows).
   * The ripple lives in its own span for 650ms, so Enterprise rewriting a button's label or
   * showing its loading spinner is never affected.
   * ------------------------------------------------------------------------------------------- */
  if (has('ja-btn-press') && !reduceMotion) {
    const rippleSelector = '.btn:not(.slider-nav__btn):not(.btn--icon), .opt-label--btn, .ja-arrow, .ja-chip, .ja-tabs__tab, .ja-hero__menu-link';
    document.addEventListener('pointerdown', (evt) => {
      if (evt.button > 0) return;
      const el = evt.target.closest && evt.target.closest(rippleSelector);
      if (!el || el.disabled || el.getAttribute('aria-disabled') === 'true' || el.classList.contains('is-loading')) return;
      const rect = el.getBoundingClientRect();
      if (getComputedStyle(el).position === 'static') el.style.position = 'relative';
      el.classList.add('ja-ripple-host');
      const wrap = document.createElement('span');
      wrap.className = 'ja-ripple-wrap';
      wrap.setAttribute('aria-hidden', 'true');
      const dot = document.createElement('span');
      dot.className = 'ja-ripple';
      dot.style.setProperty('--ja-rs', `${Math.max(rect.width, rect.height) * 2.2}px`);
      dot.style.left = `${evt.clientX - rect.left}px`;
      dot.style.top = `${evt.clientY - rect.top}px`;
      wrap.appendChild(dot);
      el.appendChild(wrap);
      setTimeout(() => wrap.remove(), 650);
    }, { passive: true });
  }

  /* ---------------------------------------------------------------------------------------------
   * Staggered reveals — give each item in a grid/slider its index so CSS can delay it.
   * ------------------------------------------------------------------------------------------- */
  if (has('ja-reveal') && !reduceMotion) {
    const stagger = () => {
      document.querySelectorAll('.ja-stagger:not(.ja-stagger--set)').forEach((list) => {
        list.classList.add('ja-stagger--set');
        Array.from(list.children).forEach((child, index) => {
          child.style.setProperty('--ja-i', Math.min(index, 12));
        });
      });
    };
    stagger();
    document.addEventListener('DOMContentLoaded', stagger);
    document.addEventListener('shopify:section:load', stagger);
  }
})();
